import React from 'react';
import type { PathMatch } from 'react-router-dom';
import { Link, matchPath, Outlet, useLocation } from 'react-router-dom';

import { useIdentifyUser } from '@/lib/posthog/hooks/useIdentifyUser';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { CompanyProfileCheckDialog } from '@/modules/companies/components/companyProfileCheck/CompanyProfileCheckDialog';
import { Copilot } from '@/modules/copilot/components/Copilot';
import { CopilotFloatButton } from '@/modules/copilot/components/CopilotFloatButton';
import { AppHeader } from '@/modules/layouts/components/AppHeader';
import {
  useIsCopilotVisible,
  useLayoutActions,
} from '@/modules/layouts/layoutStore';
import { pages } from '@/modules/layouts/pages';
import type { BreadcrumbItem as BreadcrumbItemType } from '@/modules/layouts/types';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/modules/ui/components/breadcrumb/Breadcrumb';
import Container from '@/modules/ui/components/container/Container';
import { SidebarProvider } from '@/modules/ui/components/sidebar/Sidebar';
import { useCurrentUserQuery } from '@/modules/users/queries/useCurrentUserQuery';

import Sidebar from './AppSidebar';
import { DocumentMetadata } from './DocumentMetadata';

export const AppLayout = () => {
  useIdentifyUser();

  React.useEffect(() => {
    const documentElement = document.documentElement;
    const previousDocumentOverflow = documentElement.style.overflow;
    const previousBodyOverflow = document.body.style.overflow;

    documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    return () => {
      documentElement.style.overflow = previousDocumentOverflow;
      document.body.style.overflow = previousBodyOverflow;
    };
  }, []);

  const trackEvent = useTrackEvent();

  const currentUserQuery = useCurrentUserQuery();

  const isCopilotVisible = useIsCopilotVisible();
  const { toggleCopilotVisibility } = useLayoutActions();

  const location = useLocation();

  const getCurrentPageConfig = () => {
    let currentPage = pages.find((page) => page.path === location.pathname);
    let match: PathMatch | null = null;

    if (!currentPage) {
      currentPage = pages.find((page) => {
        match = matchPath(page.path, location.pathname);
        return Boolean(match);
      });
    }

    return { currentPage, match };
  };

  const { currentPage, match } = getCurrentPageConfig();
  const breadcrumbs = currentPage?.breadcrumbs || [];

  const resolveBreadcrumbPath = (path: string) => {
    if (!match || !(match as PathMatch).params) return path;

    let resolvedPath = path;
    Object.entries((match as PathMatch).params).forEach(([key, value]) => {
      resolvedPath = resolvedPath.replace(`:${key}`, value ?? '');
    });

    return resolvedPath;
  };

  const handleCopilotOpenChange = (open: boolean) => {
    if (!open) {
      toggleCopilotVisibility();
      trackEvent('copilotClosed');
    }
  };

  return (
    <>
      <DocumentMetadata title={currentPage?.metadata?.title} />

      <SidebarProvider className="h-dvh overflow-hidden">
        <Sidebar />

        <div className="flex h-full flex-1 flex-col overflow-hidden">
          <AppHeader signedInUser={currentUserQuery.data} />

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div className="min-w-0 flex-1 overflow-y-auto">
              <Container className="pt-7 pb-0">
                {breadcrumbs.length > 0 && (
                  <Breadcrumb>
                    <BreadcrumbList>
                      {breadcrumbs.map(
                        (breadcrumb: BreadcrumbItemType, index: number) => (
                          <React.Fragment key={`${breadcrumb.path}-${index}`}>
                            <BreadcrumbItem>
                              <BreadcrumbLink asChild>
                                <Link
                                  to={resolveBreadcrumbPath(breadcrumb.path)}
                                >
                                  {breadcrumb.title}
                                </Link>
                              </BreadcrumbLink>
                            </BreadcrumbItem>
                            {index !== breadcrumbs.length - 1 && (
                              <BreadcrumbSeparator />
                            )}
                          </React.Fragment>
                        ),
                      )}
                    </BreadcrumbList>
                  </Breadcrumb>
                )}
              </Container>
              <Outlet />
            </div>
          </div>
        </div>

        <Copilot
          open={isCopilotVisible}
          onOpenChange={handleCopilotOpenChange}
        />

        {!isCopilotVisible && <CopilotFloatButton />}
        <CompanyProfileCheckDialog />
      </SidebarProvider>
    </>
  );
};
